import React, { useState } from 'react';
import {
  Button,
  Stack,
  TextField,
  Typography,
  List,
  ListItem,
  Avatar,
  ListItemText,
  CircularProgress,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import { useCollections } from '../hooks/useCollections';
import { useToast } from '@shared/hooks/useToast';
import { Product } from '@domain/product/models/Product';
import { APP_CONFIG } from '@config/app.config';

export interface CreateCollectionBottomSheetProps {
  /** Products chosen in the prior product-select step. */
  initialProducts: Product[];
  onClose: () => void;
}

const CreateCollectionBottomSheet: React.FC<CreateCollectionBottomSheetProps> = ({
  initialProducts,
  onClose,
}) => {
  const { success, error } = useToast();
  const { createCollection, addProductsToCollection } = useCollections();
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClose = () => {
    setName('');
    onClose();
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      error('Please enter a collection name');
      return;
    }
    if (initialProducts.length === 0) {
      error('Please add at least one product');
      return;
    }

    setIsSubmitting(true);
    try {
      const collection = await createCollection({ name: name.trim() });
      await addProductsToCollection({
        collectionId: collection.id,
        productIds: initialProducts.map(p => p.id),
      });
      success('Collection created');
      handleClose();
    } catch {
      error('Failed to create collection');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <DialogHeader>
        <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
          <Typography variant="h6">Create collection</Typography>
          <IconButton onClick={handleClose} size="small" aria-label="Close">
            <Close />
          </IconButton>
        </Stack>
      </DialogHeader>

      <DialogContent sx={{ flex: 1, overflow: 'auto', minHeight: 0, p: 0 }}>
        <Stack spacing={2} sx={{ p: 3 }}>
          <TextField
            label="Collection name"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g., Summer products"
            size="small"
            fullWidth
            required
            autoFocus
          />

          <Typography variant="body2" color="text.secondary">
            {initialProducts.length} product{initialProducts.length !== 1 ? 's' : ''} selected
          </Typography>

          <List
            dense
            disablePadding
            sx={{
              maxHeight: 320,
              overflow: 'auto',
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            {initialProducts.map(p => (
              <ListItem key={p.id} sx={{ py: 1 }}>
                <Avatar
                  src={p.image}
                  variant="rounded"
                  sx={{
                    width: 40,
                    height: 40,
                    border: 1,
                    borderColor: APP_CONFIG.ui.theme.colors.border,
                    mr: 2,
                  }}
                />
                <ListItemText
                  primary={p.title || 'Untitled'}
                  secondary={p.ean}
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItem>
            ))}
          </List>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ borderTop: 1, borderColor: 'divider', p: 2, justifyContent: 'flex-end' }}>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleSubmit()}
          disabled={!name.trim() || initialProducts.length === 0 || isSubmitting}
        >
          {isSubmitting ? <CircularProgress size={24} /> : 'Create collection'}
        </Button>
      </DialogActions>
    </>
  );
};

export default CreateCollectionBottomSheet;
